import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Toolbar,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { getValidToken } from "../../utils/authUtils";
import {
  commonTextFieldProps,
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../utils/styles";
import { formatDate } from "../../utils/utils";
import { Loading } from "../../components/Loading";

// Types
interface JobReport {
  "S. NO": number;
  "Call Date": string;
  "JOB ID": string;
  "Emergency Type": string;
  Category: string;
  "Sub Category": string;
  "Primary Complaint": string;
  "JOB Status": string;
  "Status (Availed/Unavailed)": string;
  Area: string;
  "Vehicle Type": string;
  "Caller Name": string;
  "Emergency Loc District": string;
  Project: string;
  // ... other fields
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ApiResponse {
  success: boolean;
  data: JobReport[];
  pagination: PaginationInfo;
  error?: string;
}

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  ...tableHeaderStyles,
}));

const FilterToolbar = styled(Toolbar)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
  gap: theme.spacing(2),
  flexWrap: "wrap",
}));

// Table columns configuration
const columns = [
  { id: "Call Date", label: "Call Date", minWidth: 120 },
  { id: "JOB ID", label: "Job ID", minWidth: 120 },
  { id: "Emergency Type", label: "Emergency Type", minWidth: 140 },
  { id: "Category", label: "Category", minWidth: 120 },
  { id: "Primary Complaint", label: "Primary Complaint", minWidth: 180 },
  { id: "JOB Status", label: "Status", minWidth: 100 },
  { id: "Area", label: "Area", minWidth: 120 },
  { id: "Vehicle Type", label: "Vehicle Type", minWidth: 120 },
  { id: "Caller Name", label: "Caller Name", minWidth: 140 },
  { id: "Emergency Loc District", label: "District", minWidth: 140 },
  { id: "Project", label: "Project", minWidth: 120 },
];

const JobReportsPage: React.FC = () => {
  const [data, setData] = useState<JobReport[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Filter states
  const [projectFilter, setProjectFilter] = useState<string>("Bihar");
  const [emergencyTypeFilter, setEmergencyTypeFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Available filter options (these would come from API in real app)
  const [projects, setProjects] = useState<string[]>(["Bihar", "Up", "Punjab"]);
  const [emergencyTypes, setEmergencyTypes] = useState<string[]>([]);
  const [statusOptions] = useState<string[]>([
    "Completed",
    "In Progress",
    "Cancelled",
    "Pending",
  ]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(projectFilter && { project: projectFilter }),
        ...(emergencyTypeFilter && { emergencyType: emergencyTypeFilter }),
        ...(statusFilter && { jobStatus: statusFilter }),
        ...(searchTerm && { search: searchTerm }),
      });

      const token = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}job-reports?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result: ApiResponse = await response.data;

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch data");
      }

      setData(result.data);
      setTotalCount(result.pagination.totalRecords);

      // Extract unique values for filters
      const uniqueEmergencyTypes = Array.from(
        new Set(
          result.data.map((item) => item["Emergency Type"]).filter(Boolean)
        )
      );

      setEmergencyTypes(uniqueEmergencyTypes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching job reports:", err);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    projectFilter,
    emergencyTypeFilter,
    statusFilter,
    searchTerm,
  ]);

  // Effects
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Event handlers
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleProjectFilterChange = (event: SelectChangeEvent<string>) => {
    setProjectFilter(event.target.value);
    setPage(0);
  };

  const handleEmergencyTypeFilterChange = (
    event: SelectChangeEvent<string>
  ) => {
    setEmergencyTypeFilter(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const clearFilters = () => {
    setProjectFilter("");
    setEmergencyTypeFilter("");
    setStatusFilter("");
    setSearchTerm("");
    setPage(0);
  };

  const getStatusChipColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "in progress":
        return "warning";
      case "cancelled":
        return "error";
      case "pending":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      {/* <Typography variant="h4" component="h1" gutterBottom>
        Job Reports Dashboard
      </Typography> */}

      {/* Filter Toolbar */}
      <Paper sx={{ p: 1.5, mb: 1 }}>
        <Grid container spacing={2} alignItems="center">
          {/* <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by caller name, job ID..."
              {...commonTextFieldProps}
            />
          </Grid> */}

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: "0.75rem" }}>Project</InputLabel>
              <Select
                value={projectFilter}
                label="Project"
                onChange={handleProjectFilterChange}
                {...commonTextFieldProps.SelectProps}
              >
                {projects.map((project) => (
                  <MenuItem
                    sx={{ fontSize: "0.75rem" }}
                    key={project}
                    value={project}
                  >
                    {project}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1} sx={{ marginLeft: "auto" }}>
            <Button {...smallButtonProp} variant="outlined" onClick={clearFilters}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ width: "100%", mb: 2 }}>
        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <Loading fullScreen={false} message="Loading..." />
          </Box>
        )}

        {/* Table */}
        <TableContainer sx={{ overflowY: "hidden" }}>
          <Table size="small" stickyHeader aria-label="job reports table">
            <TableHead sx={{ ...tableHeaderStyles, fontSize: "0.75rem" }}>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      ...tableHeaderStyles,
                      fontSize: "0.75rem",
                      minWidth: column.minWidth,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      sx={{ py: 3 }}
                    >
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    {columns.map((column) => {
                      const value = row[column.id as keyof JobReport];

                      return (
                        <TableCell
                          key={column.id}
                          sx={{ ...tableCellStyles }}
                        >
                          {column.id === "JOB Status" ? (
                            <Chip
                              label={value}
                              color={getStatusChipColor(value as string) as any}
                              size="small"
                              sx={{ ...tableCellStyles }}
                            />
                          ) 
                          // : column.id === "Call Date" ? (
                          //   formatDate(value as string)
                          // ) 
                          : (
                            value || "-"
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      </Paper>
    </Box>
  );
};

export default JobReportsPage;
